import { SleepCheckupUserStore, signOut } from "@/utils/auth";
import constants from "@/utils/constants";
import {
  Add,
  AssignmentOutlined,
  Close,
  Download,
  Face,
} from "@mui/icons-material";
import {
  Button,
  Card,
  CardContent,
  IconButton,
  Link,
  Stack,
  Tab,
  TableContainerProps,
  Tabs,
  Typography,
  TypographyProps,
} from "@mui/material";
import { format, parse } from "date-fns";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { UAParser } from "ua-parser-js";
import { ScrollToTop } from "../common/ScrollToTop";
import { TableContainer } from "../report/disease_risk/disease_risk_v1/medical_facility_report/commons";
import {
  AccountMenuButton,
  AccountMenuList,
  SCAppBar,
} from "../sleep_checkup_v1/SCAppBar";
import FactAndComments from "./FactAndComments";
import FactAndComments2 from "./FactAndComments2";
import IssueFromUser from "./IssueFromUser";
import Plans from "./Plans";
import Retrospectives from "./Retrospectives";
import Retrospectives2 from "./Retrospectives2";
import SleepSummaries from "./SleepSummaries";
import SleepSummaries2 from "./SleepSummaries2";
import { useUserInfo } from "./apis";
import { UserInfo } from "./commons/user_infos";

// 画面を最下部まで移動
async function scrollToBottom() {
  window.scrollTo(0, 0); // 最上部に戻す
  const scrollHeight = document.documentElement.scrollHeight;
  const innerHeight = window.innerHeight;
  for (let current = 0; current < scrollHeight; current += innerHeight) {
    window.scrollTo(0, current);
    await new Promise((r) => setTimeout(r, 300)); // 描画待ち
  }
  window.scrollTo(0, 0); // 最上部に戻す
}

// canvas の内容をページごとに分割してPDFに変換する
function convertToPDF(canvas: HTMLCanvasElement) {
  const pdf = new jsPDF("p", "mm", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 10;

  const usableWidth = pageWidth - margin * 2;
  const usableHeight = pageHeight - margin * 2;

  const imgWidth = usableWidth;
  const imgHeight = (canvas.height * usableWidth) / canvas.width;

  const pageCount = Math.ceil(imgHeight / usableHeight);

  for (let i = 0; i < pageCount; i++) {
    const sourceY = (i * usableHeight * canvas.width) / usableWidth;
    const pageCanvas = document.createElement("canvas");
    pageCanvas.width = canvas.width;
    pageCanvas.height = (usableHeight * canvas.width) / usableWidth;

    const ctx = pageCanvas.getContext("2d");
    ctx?.drawImage(canvas, 0, -sourceY);

    const pageImgData = pageCanvas.toDataURL("image/png");
    pdf.addImage(pageImgData, "PNG", margin, margin, imgWidth, usableHeight);

    if (i < pageCount - 1) pdf.addPage();
  }

  return pdf;
}

function downloadPDF(pdf: jsPDF) {
  const parser = new UAParser(window.navigator.userAgent);
  const parserResults = parser.getResult();
  const browserName = parserResults.browser.name;
  const blob = pdf.output("blob");
  const url = URL.createObjectURL(blob);
  if (browserName?.includes("Safari")) {
    const a = document.createElement("a");
    a.href = url;
    a.download = "worksheet.pdf";
    a.target = "_blank";
    document.body.appendChild(a);
    try {
      a.click();
    } finally {
      a.remove();
    }
  } else {
    window.open(url, "_blank");
  }
}

// エクスポート
function ExportButton({
  contentRef,
}: {
  contentRef: RefObject<HTMLDivElement | null>;
}) {
  const [processing, setProcessing] = useState(false);
  const capture = useCallback(async () => {
    if (contentRef.current == null) return;

    setProcessing(true);

    // textarea を div に置換
    const textareas = contentRef.current.querySelectorAll("textarea");
    const replacedElements: {
      div: HTMLDivElement;
      textarea: HTMLTextAreaElement;
    }[] = [];

    textareas.forEach((textarea) => {
      if (textarea.getAttribute("aria-hidden") === "true") return;

      const div = document.createElement("div");
      div.style.width = `${textarea.getBoundingClientRect().width}px`;
      div.style.height = `${textarea.getBoundingClientRect().height}px`;
      div.textContent = textarea.value;
      div.style.wordBreak = "break-word";
      div.style.whiteSpace = "pre-wrap";
      textarea.parentNode?.replaceChild(div, textarea);
      replacedElements.push({ div, textarea });
    });

    // DOM更新後のレンダリング待機
    await new Promise((resolve) => setTimeout(resolve, 100));

    // 全画面を描画するため、いったん下までスクロールする
    await scrollToBottom();

    try {
      // キャプチャ（タイムアウト付き）
      const canvas = await Promise.race([
        html2canvas(contentRef.current, {
          allowTaint: true,
          useCORS: true,
          scale: 1, // メモリ使用量を抑えるためscaleを1に設定
        }),
        new Promise<never>((_, reject) =>
          setTimeout(
            () =>
              reject(
                new Error(
                  "エクスポートが10秒以内に完了しませんでした。お使いの端末ではエクスポート機能が使えない可能性があります。"
                )
              ),
            10000
          )
        ),
      ]);

      const pdf = convertToPDF(canvas);

      downloadPDF(pdf);
    } catch (error) {
      alert(`エクスポートに失敗しました: ${error}`);
    } finally {
      // div を textarea に戻す
      replacedElements.forEach(({ div, textarea }) => {
        div.parentNode?.replaceChild(textarea, div);
      });
      setProcessing(false);
    }
  }, [contentRef]);

  return (
    <Button
      startIcon={<Download />}
      onClick={!processing ? capture : undefined}
      disabled={processing}
      sx={{ pl: 6 }}
    >
      エクスポート
    </Button>
  );
}

const MIN_WIDTH = 1098;

/** タブ種別 */
type TabType =
  | "sleep_summaries"
  | "issue_from_user"
  | "retrospectives"
  | "fact_and_comments"
  | "plan";

/**
 * ワークシート画面
 * @returns ワークシート画面
 */
export default function Worksheet() {
  const { uuid, tab = "sleep_summaries" } = useParams<{
    uuid: string;
    tab: string;
  }>();

  const api = useUserInfo();
  const [userInfo, setUserInfo] = useState<UserInfo>();
  const [showExamineeInfo, setShowExamineeInfo] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  const onChangeTab = useCallback(
    (_: React.SyntheticEvent, newTab: TabType) => {
      navigate(`/facility/worksheet/${uuid}/${newTab}`);
    },
    [uuid, navigate]
  );

  const getMeasurementPeriod = useCallback(() => {
    const currentMeasurement = userInfo?.measurement_history[0];
    if (currentMeasurement == null) return "";

    function fmt(date: string) {
      return date.replace(/(\d+)-(\d+)-(\d+).*/, "$1年$2月$3日");
    }

    return `${fmt(currentMeasurement.period_start_date)} 〜 ${fmt(
      currentMeasurement.period_end_date
    )}`;
  }, [userInfo]);

  useEffect(() => {
    (async () => {
      if (uuid == null) return;
      const res = await api.get(uuid);
      setUserInfo(res.data);
    })();
  }, [uuid, api]);

  if (!userInfo) return null;

  // タブグループの出し分け
  const reportVersion = userInfo?.measurement_history[0].report_version;
  const isReportVersionLow = ["1.0", "1.1", "2.0", "3.0", "3.1"].includes(
    reportVersion || ""
  );

  // URL から該当タブのキーを取得
  const tabContent = (
    {
      sleep_summaries: () =>
        isReportVersionLow ? <SleepSummaries /> : <SleepSummaries2 />,
      issue_from_user: () => <IssueFromUser />,
      retrospectives: () =>
        isReportVersionLow ? <Retrospectives /> : <Retrospectives2 />,
      fact_and_comments: () =>
        isReportVersionLow ? <FactAndComments /> : <FactAndComments2 />,
      plan: () => <Plans />,
    }[tab] ?? (() => null)
  )();

  return (
    <>
      <AppBar />
      <ScrollToTop />
      <Stack ref={contentRef}>
        <Stack
          sx={{
            position: "sticky",
            backgroundColor: "#FFFFFF",
            top: "68px",
            px: 10,
            py: 6,
            minWidth: MIN_WIDTH,
            zIndex: 10,
          }}
        >
          <Stack direction="row" alignItems="center">
            <Typography variant="h3">睡眠改善ワークシート</Typography>
            {userInfo && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  ml: 2,
                }}
              >
                {` [ ${
                  userInfo?.medical_examinee_name
                }│測定期間：${getMeasurementPeriod()} ] `}
              </Typography>
            )}
            <ExportButton contentRef={contentRef} />
          </Stack>

          <Stack>
            <Tabs value={tab} onChange={onChangeTab}>
              <Tab value="sleep_summaries" label="睡眠評価"></Tab>
              {!isReportVersionLow && (
                <Tab value="issue_from_user" label="気になる課題"></Tab>
              )}
              <Tab value="retrospectives" label="原因分析"></Tab>
              <Tab value="fact_and_comments" label="週間グラフ"></Tab>
              <Tab value="plan" label="改善目標"></Tab>
            </Tabs>
          </Stack>
        </Stack>

        <Stack
          sx={{
            mx: 10,
            mb: 20,
            minWidth: MIN_WIDTH,
          }}
        >
          <Stack>
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              {/* タブ内容 */}
              <Stack sx={{ width: "100%" }}>{tabContent}</Stack>
              {/* 受診者情報 */}
              <Stack>
                {!showExamineeInfo && (
                  <ShowExamineeInfoButton
                    onClick={() => setShowExamineeInfo(true)}
                  />
                )}
                {showExamineeInfo && (
                  <ExamineeInfoCard
                    userInfo={userInfo}
                    onClickClose={() => setShowExamineeInfo(false)}
                  />
                )}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </>
  );
}

const fmt = (ymd: string) =>
  format(parse(ymd, "yyyy-MM-dd", new Date()), "yyyy年M月d日");

function AppBar() {
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const navigate = useNavigate();
  const user = SleepCheckupUserStore.getItem();

  useEffect(() => {
    if (user == null) {
      // 他施設のログイン画面（サブドメ）にアクセスできないように、未ログインの場合はログイン画面にリダイレクトする
      navigate("/facility/signin");
    }
  }, [user, navigate]);

  const handleAccountMenuOpen = () => {
    setAccountMenuOpen(true);
  };

  const handleAccountMenuClose = () => {
    setAccountMenuOpen(false);
  };

  const handleLogout = () => {
    signOut();
    navigate(constants.SIGNIN_URL_MEDICAL_FACILITY_USER);
  };

  return (
    <SCAppBar
      accountMenuButton={
        <AccountMenuButton
          accountMenuList={<AccountMenuList onClickLogout={handleLogout} />}
          openMenu={accountMenuOpen}
          onClick={handleAccountMenuOpen}
          onClose={handleAccountMenuClose}
        >
          {user?.username}
        </AccountMenuButton>
      }
      minWidth={MIN_WIDTH}
    />
  );
}

interface ShowExamineeInfoButtonProps {
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}
function ShowExamineeInfoButton({ onClick }: ShowExamineeInfoButtonProps) {
  return (
    <Button
      variant="contained"
      size="medium"
      color="primary"
      aria-label="show"
      onClick={onClick}
      sx={{
        "& .MuiButton-startIcon": {
          marginRight: 0,
          marginLeft: 0,
        },
        height: "auto",
        width: "68px",
        padding: "16px",
        borderRadius: "20px 0 0 20px",
        position: "fixed",
        top: "50%",
        left: "calc(100% - 68px)",
        transform: "translateY(-50%)",
      }}
    >
      <Typography sx={{ writingMode: "vertical-rl" }}>
        <Add fontSize="small" sx={{ m: "8px" }} />受 診 者 情 報 を 見 る
      </Typography>
    </Button>
  );
}

function ExamineeInfoSection({
  userInfo,
  onClickClose,
}: ExamineeInfoCardProps) {
  return (
    <Stack>
      <Stack direction="row" sx={{ justifyContent: "space-between" }}>
        <Stack
          direction="row"
          sx={{
            gap: 2,
            alignItems: "center",
          }}
        >
          <Face />
          <Typography variant="subtitle2" sx={{ color: "#7A7A7A" }}>
            受診者情報
          </Typography>
        </Stack>
        <IconButton onClick={onClickClose}>
          <Close />
        </IconButton>
      </Stack>

      <Stack>
        <LabelTypography variant="caption">
          {userInfo?.medical_examinee_name_kana}
        </LabelTypography>
        <LabelTypography variant="h4">
          {userInfo?.medical_examinee_name}
        </LabelTypography>
        <table>
          <tbody>
            <tr>
              <td>
                <Typography variant="body2" sx={{ color: "#7A7A7A" }}>
                  生年月日
                </Typography>
              </td>
              <td>
                <LabelTypography variant="body2">
                  {`${userInfo?.medical_examinee_birthday}（${userInfo?.medical_examinee_age_at_sleep_checkup}歳）`}
                </LabelTypography>
              </td>
            </tr>
            <tr>
              <td>
                <Typography variant="body2" sx={{ color: "#7A7A7A" }}>
                  企業名
                </Typography>
              </td>
              <td>
                <LabelTypography variant="body2">
                  {userInfo?.corporate_name}
                </LabelTypography>
              </td>
            </tr>
            <tr>
              <td>
                <Typography variant="body2" sx={{ color: "#7A7A7A" }}>
                  部署名
                </Typography>
              </td>
              <td>
                <LabelTypography variant="body2">
                  {userInfo?.department_name}
                </LabelTypography>
              </td>
            </tr>
          </tbody>
        </table>
      </Stack>
    </Stack>
  );
}

function InterviewInfoSection({ userInfo }: UserInfoProps) {
  return (
    <Stack>
      <Stack direction="row" sx={{ justifyContent: "space-between" }}>
        <Stack
          direction="row"
          sx={{
            gap: 2,
            alignItems: "center",
          }}
        >
          <AssignmentOutlined />
          <Typography variant="subtitle2" sx={{ color: "#7A7A7A" }}>
            問診情報
          </Typography>
        </Stack>
      </Stack>
      <Stack
        sx={{
          "&>table>tbody>tr>td:first-of-type": { width: "121.5px" },
          "&>table>tbody>tr>td:nth-of-type(2)": { verticalAlign: "top" },
        }}
      >
        <table>
          <tbody>
            <tr>
              <td>
                <Typography variant="body2" sx={{ color: "#7A7A7A" }}>
                  既往歴
                </Typography>
              </td>
              <td>
                <LabelTypography variant="body2">
                  {userInfo?.interview_info.disease_besides_hypertension_name}
                </LabelTypography>
              </td>
            </tr>
            <tr>
              <td>
                <Typography variant="body2" sx={{ color: "#7A7A7A" }}>
                  睡眠に関する既往歴
                </Typography>
              </td>
              <td>
                <LabelTypography variant="body2">
                  {userInfo?.interview_info.under_treatment_about_sleep}
                </LabelTypography>
              </td>
            </tr>
            <tr>
              <td>
                <Typography variant="body2" sx={{ color: "#7A7A7A" }}>
                  睡眠薬の継続使用
                </Typography>
              </td>
              <td>
                <LabelTypography variant="body2">
                  {userInfo?.interview_info.sleep_medication}
                </LabelTypography>
              </td>
            </tr>
            <tr>
              <td>
                <Typography variant="body2" sx={{ color: "#7A7A7A" }}>
                  朝型/夜型の自己認識
                </Typography>
              </td>
              <td>
                <LabelTypography variant="body2">
                  {userInfo?.interview_info.three_dss_morning_type}
                </LabelTypography>
              </td>
            </tr>
            <tr>
              <td>
                <Typography variant="body2" sx={{ color: "#7A7A7A" }}>
                  子育て/介護による睡眠への影響
                </Typography>
              </td>
              <td>
                <LabelTypography variant="body2">
                  {userInfo?.interview_info.habitually_get_up_to_take_care}
                </LabelTypography>
              </td>
            </tr>
            <tr>
              <td>
                <Typography variant="body2" sx={{ color: "#7A7A7A" }}>
                  睡眠について気になること
                </Typography>
              </td>
              <td>
                <LabelTypography variant="body2">
                  {userInfo?.interview_info.concern_about_sleep}
                </LabelTypography>
              </td>
            </tr>
          </tbody>
        </table>
      </Stack>
    </Stack>
  );
}

function MeasurementHistory({ userInfo }: UserInfoProps) {
  return (
    <Stack sx={{ gap: 2 }}>
      <Stack direction="row" sx={{ justifyContent: "space-between" }}>
        <Stack
          direction="row"
          sx={{
            gap: 2,
            alignItems: "center",
          }}
        >
          <AssignmentOutlined />
          <Typography variant="subtitle2" sx={{ color: "#7A7A7A" }}>
            測定履歴
          </Typography>
        </Stack>
      </Stack>
      {userInfo?.measurement_history.map((history, i) => (
        <Stack key={i} sx={{ "& *": { whiteSpace: "nowrap" } }}>
          {i === 0 ? (
            <Typography variant="body2">
              {fmt(history.period_start_date)}〜{fmt(history.period_end_date)}
              （今回）
            </Typography>
          ) : (
            <Stack direction="row">
              {history.is_worksheet_openable ? (
                <Link
                  href={`/facility/worksheet/${history.sleep_checkup_info_uuid}/sleep_summaries`}
                  target="_blank"
                  variant="body2"
                >
                  {fmt(history.period_start_date)}〜
                  {fmt(history.period_end_date)}
                </Link>
              ) : (
                <Typography variant="body2">
                  {fmt(history.period_start_date)}〜
                  {fmt(history.period_end_date)}
                </Typography>
              )}
              {i === 1 && <Typography variant="body2">（前回）</Typography>}
            </Stack>
          )}
        </Stack>
      ))}
    </Stack>
  );
}

interface UserInfoProps {
  userInfo: UserInfo | undefined;
}
interface ExamineeInfoCardProps extends UserInfoProps {
  onClickClose: () => void;
}
function ExamineeInfoCard({ userInfo, onClickClose }: ExamineeInfoCardProps) {
  return (
    <Card sx={{ position: "sticky", top: "203px", maxWidth: "315px" }}>
      <CardContent>
        <Stack sx={{ gap: 6 }}>
          {/** 受診者情報 */}
          <ExamineeInfoSection
            userInfo={userInfo}
            onClickClose={onClickClose}
          />
          {/** 問診情報 */}
          <InterviewInfoSection userInfo={userInfo} />
          {/** 測定履歴 */}
          <MeasurementHistory userInfo={userInfo} />
        </Stack>
      </CardContent>
    </Card>
  );
}

interface LabelTypographyProps extends TypographyProps {
  children: string | null | undefined;
}
function LabelTypography({ children, ...props }: LabelTypographyProps) {
  return <Typography {...props}>{children ?? "-"}</Typography>;
}

export interface BorderlessTableContainerProps extends TableContainerProps {}
/**
 * 睡眠評価用のテーブルコンテナ
 * @param props {@link BorderlessTableContainer}
 * @returns 睡眠評価用のテーブルコンテナ
 */
export function BorderlessTableContainer({
  sx,
  ...props
}: BorderlessTableContainerProps) {
  return (
    <TableContainer
      sx={{
        boxShadow: "none",
        border: "solid 1px #E0E0E0",
        "& .MuiTableCell-root": {
          borderBottom: "initial",
        },
        "& .MuiTableHead-root .MuiTableRow-root, & .MuiTableBody-root .MuiTableRow-root:not(:last-child)":
          {
            borderBottom: "solid 1px #E0E0E0",
          },
        ...sx,
      }}
      {...props}
    ></TableContainer>
  );
}
